/**
 * useHostBackground — host 大底背景樣式(PC / mobile 分開,目前同圖)。
 *
 * 有設定 gameAssets.background 就用該圖,沒設定 fallback 到深底色 `#1a1c2a`,
 * 確保 native 遊戲 config 內 background 未配 / 圖檔還沒進 repo 時也能正常顯示。
 *
 * # 為什麼回傳兩個 computed
 *
 * `pcBackgroundStyle` / `mobileBackgroundStyle` 目前邏輯相同,但分開暴露兩個
 * 變數讓未來想分流(例如 PC 走風景圖、mobile 走縱向構圖)的時候不用動 App.vue
 * template,只動 composable 內部即可。Vue 的 computed 是 lazy 而且共享 setter,
 * 維持兩個 ref 的 cost 可忽略。
 *
 * # 用法(App.vue)
 *
 *   const { pcBackgroundStyle, mobileBackgroundStyle } = useHostBackground(gameAssets)
 *
 *   <template>
 *     <div class="host-bg host-bg-pc" :style="pcBackgroundStyle" />
 *     <div class="host-bg host-bg-mobile" :style="mobileBackgroundStyle" />
 *   </template>
 */

import { computed, type ComputedRef, type Ref } from 'vue'
import { resolveImg } from '@/composables/useGameImages'

const FALLBACK_BG_COLOR = '#1a1c2a'

interface BackgroundAssets {
  background?: string
}

export interface UseHostBackgroundReturn {
  pcBackgroundStyle: ComputedRef<Record<string, string>>
  mobileBackgroundStyle: ComputedRef<Record<string, string>>
}

export function useHostBackground(
  assets: Readonly<Ref<BackgroundAssets>>,
): UseHostBackgroundReturn {
  const styleOf = (): Record<string, string> => {
    const bg = assets.value.background
    return bg ? { backgroundImage: `url(${resolveImg(bg)})` } : { backgroundColor: FALLBACK_BG_COLOR }
  }
  return {
    pcBackgroundStyle: computed(styleOf),
    mobileBackgroundStyle: computed(styleOf),
  }
}
